import { PLAQUE } from './hud-theme';
import { ITEM_DEFS, itemLabel } from '../config/item-defs';
import { itemIconSrc, rarityFrameClass, esc, ICON_FALLBACK_ATTR } from './item-icon';
import { t, getIntlLocale } from '../i18n/i18n';

/**
 * "You picked this up" — said once, then gone (2026-09-07).
 *
 * User: *"we can show notifications about what was added to the inventory,
 * how much inventory space I have left, and similar information."*
 *
 * This is the half of the always-open bag list that anyone was actually
 * reading. A list answers "what am I carrying", which is a question you ask a
 * few times a run; a toast answers "what did I just get", which is the
 * question every chest asks and the only one that is time-sensitive. Splitting
 * them is what lets the panel close (`loot-panel.ts`).
 *
 * Each toast carries the object's own picture and rarity frame — the same one
 * the bag, the Stash and the shop use — so a gold frame sliding in *is* the
 * "that was a rare" moment, with no word for it.
 *
 * The remaining-capacity line rides along on the newest toast rather than
 * getting a notification of its own: the moment a player wants to know how
 * much bag is left is the moment something just went into it.
 */

export interface LootToastView {
  itemDefId: string;
  weightKg: number;
  /** Kilos still free after this pickup. Omitted when capacity is not known yet. */
  remainingKg?: number;
}

/** Long enough to read two short lines, short enough not to stack up during a chest run. */
const TOAST_MS = 3200;
/**
 * Beyond this, older toasts are dropped rather than queued.
 *
 * A Vaults run now holds forty chests. A queue would put a delver several
 * seconds behind their own bag, announcing a candle they picked up in the
 * last room — and the whole value of a toast is that it is about *now*.
 */
const MAX_VISIBLE = 4;

export class LootToasts {
  private readonly root: HTMLDivElement;
  private readonly timers = new Map<HTMLElement, ReturnType<typeof setTimeout>>();

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.dataset.hud = 'loot-toasts';
    this.root.className = [
      'fixed', 'right-3', 'bottom-[21rem]', 'sm:bottom-[8.5rem]', 'z-[1010]',
      'pointer-events-none', 'select-none',
      'flex', 'flex-col', 'items-end', 'gap-1.5',
      'w-[min(88vw,320px)]',
    ].join(' ');
    container.appendChild(this.root);
  }

  push(view: LootToastView): void {
    const def = ITEM_DEFS[view.itemDefId];
    const rarity = def?.rarity ?? 'COMMON';
    const name = itemLabel(view.itemDefId); // gear included — see loot-panel's labelFor

    const toast = document.createElement('div');
    toast.dataset.lootToast = view.itemDefId;
    toast.className = [
      PLAQUE, 'px-2.5', 'py-1.5', 'flex', 'items-center', 'gap-2', 'w-full',
      // Slides in from the side the bag lives on — see `animate-loot-toast`
      // in styles/app.css, which also turns itself off under
      // prefers-reduced-motion.
      'animate-loot-toast',
    ].join(' ');

    const remaining = view.remainingKg === undefined
      ? ''
      : `<p class="text-[11px] text-text-dim tabular-nums">${esc(t('{kg}kg of bag left', { kg: kg(view.remainingKg) }))}</p>`;

    toast.innerHTML = `
      <div class="relative shrink-0 w-8 h-8 rounded border ${rarityFrameClass(rarity)} overflow-hidden">
        <img ${ICON_FALLBACK_ATTR} src="${esc(itemIconSrc(view.itemDefId))}" alt="" aria-hidden="true"
             class="absolute inset-0 w-full h-full object-contain p-0.5" />
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-[12.5px] truncate ${rarity === 'RARE' ? 'text-gold' : 'text-text'}">+ ${esc(name)}</p>
        <p class="text-[11px] text-text-dim tabular-nums">${esc(t('{kg}kg', { kg: kg(view.weightKg) }))}</p>
      </div>
      ${remaining}`;

    this.root.appendChild(toast);
    this.timers.set(toast, setTimeout(() => this.remove(toast), TOAST_MS));
    // Oldest first, so what is dropped is the one that has been readable
    // longest rather than the one that just arrived.
    while (this.root.children.length > MAX_VISIBLE) {
      this.remove(this.root.firstElementChild as HTMLElement);
    }
  }

  private remove(toast: HTMLElement | null): void {
    if (!toast) return;
    const timer = this.timers.get(toast);
    if (timer !== undefined) clearTimeout(timer);
    this.timers.delete(toast);
    toast.remove();
  }

  dispose(): void {
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
    this.root.remove();
  }
}

/** One decimal, in the player's own number format. */
function kg(value: number): string {
  return value.toLocaleString(getIntlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}
